import { transition } from '@helpers/animate';
import { imageCoverContainer, imageWrapperPreserveRatio } from '@helpers/aspectRatio';
import brand, { colourWithTransparency } from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { ButtonReset } from '@helpers/global';
import { Device, from, until } from '@helpers/media';
import { contentStyles, paragraphSmall } from '@helpers/typography';
import styled, { css } from 'styled-components';
const Container = styled.div `
  --backgroundColour: ${brand.global.white};
  --collectionTagFgColour: ${brand.global.black};
  color: currentColor;
  text-decoration: none;

  /* Background.Teal */
  &[data-background='teal'] {
    --backgroundColour: ${brand.primary.teal.teal25};
    --darkBackgroundColour: ${brand.primary.teal.teal100};
  }

  /* Background.Pear */
  &[data-background='pear'] {
    --backgroundColour: ${brand.primary.pear.pear25};
    --darkBackgroundColour: ${brand.primary.pear.pear100};
  }

  /* Background.Blue */
  &[data-background='blue'] {
    --backgroundColour: ${brand.primary.blue.blue25};
    --darkBackgroundColour: ${brand.primary.blue.blue100};
  }

  /* Background.Purple */
  &[data-background='purple'] {
    --backgroundColour: ${brand.secondary.purple.purple25};
    --darkBackgroundColour: ${brand.secondary.purple.purple100};
    --collectionTagFgColour: ${brand.global.white};
  }

  /* Background.Bronze */
  &[data-background='bronze'] {
    --backgroundColour: ${brand.awards.bronze.bronze25};
    --darkBackgroundColour: ${brand.awards.bronze.bronze100};
  }

  /* Background.Silver */
  &[data-background='silver'] {
    --backgroundColour: ${brand.awards.silver.silver25};
    --darkBackgroundColour: ${brand.awards.silver.silver100};
  }

  /* Background.Gold */
  &[data-background='gold'] {
    --backgroundColour: ${brand.awards.gold.gold25};
    --darkBackgroundColour: ${brand.awards.gold.gold100};
  }

  display: flex;
  flex-direction: column;
  height: 100%;
`;
const ImageContainer = styled.div `
  position: relative;
`;
const LinkArea = styled.a `
  border-radius: 20px;
  background-color: var(--backgroundColour);
  padding: ${fluid(10, 18)} ${fluid(10, 24)};
  position: relative;
  height: ${fluid(337, 438)};
  z-index: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  gap: 10px;
  text-decoration: none;
  color: ${brand.global.black};
`;
const ImageWrapper = styled.div `
  ${imageWrapperPreserveRatio(308, 251)};
  max-height: 251px;

  @media ${from(Device.TabletLarge)} {
    ${imageWrapperPreserveRatio(536, 316)};
    max-height: 316px;
  }
`;
const Image = styled.img `
  ${imageCoverContainer()};

  object-fit: contain;
`;
const CollectionTag = styled.div `
  ${fonts.gotham.bold};
  background-color: var(--darkBackgroundColour);
  color: var(--collectionTagFgColour);
  font-size: ${fluid(14, 16)};
  line-height: ${fluid(21, 26)};
  position: absolute;
  bottom: 0;
  left: ${fluid(10, 24)};
  padding: ${fluid(5, 8)} 16px ${fluid(3, 6)};
  border-radius: 10px 10px 0 0;
`;
const ButtonArea = styled.div `
  display: flex;
  gap: 8px;
  position: absolute;
  right: ${fluid(8, 24)};
  bottom: ${fluid(12, 24)};
  z-index: 1;

  @media ${until(Device.Tablet)} {
    flex-direction: column;
  }
`;
const EyeClose = styled.span ``;
const EyeOpen = styled.span ``;
const buttonStyles = css `
  ${transition('background-color')};
  display: flex;
  justify-content: center;
  align-items: center;
  width: ${fluid(40, 60)};
  height: ${fluid(40, 60)};
  color: ${brand.global.white};
  background-color: ${brand.global.black};
  border-radius: 1000000000px;
  border: ${fluid(2, 4)} solid ${brand.global.black};

  ${EyeClose} {
    display: block;
  }

  ${EyeOpen} {
    display: none;
  }

  &:hover,
  &.focus-ring {
    background-color: ${colourWithTransparency(brand.global.black, 0.50)};

    ${EyeClose} {
      display: none;
    }

    ${EyeOpen} {
      display: block;
    }
  }

  svg,
  ${EyeClose},
  ${EyeOpen} {
    width: 20px;
    height: 20px;
  }
`;
const ViewResourceButton = styled.a `
  ${ButtonReset};
  ${buttonStyles};

  ${LinkArea}:hover + ${ButtonArea} & ,
  ${LinkArea}.focus + ${ButtonArea} & {
    background-color: ${colourWithTransparency(brand.global.black, 0.50)};

    ${EyeClose} {
      display: none;
    }

    ${EyeOpen} {
      display: block;
    }
  }
`;
const DownloadResourceButton = styled.a `
  ${buttonStyles};
  
`;
const ContentContainer = styled.div `
  position: relative;
  overflow: hidden;
  margin-top: ${fluid(16, 24)};
  display: flex;
  flex-direction: column;

  @media ${until(Device.Tablet)} {
    gap: 8px;
  }
`;
const TitleArea = styled.div `
  display: flex;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: ${fluid(12, 20)};
`;
const Title = styled.h2 `
  ${fonts.gotham.black};
  grid-column: span 4;
  font-size: ${fluid(18, 24)};
  line-height: ${fluid(27, 30)};
  margin: 0;

  @media ${until(Device.Tablet)} {
    ${fonts.gotham.bold};
  }

  [data-type='engage'] & {
    ${fonts.neueGalano.extraBold};
  }
`;
const CardLink = styled.a `
  text-decoration: none;
  color: ${brand.global.black};

  &:hover {
    text-decoration: underline;
  }
`;
const AgeTag = styled.div `
  --tagBgColour: ${brand.primary.pear.pear50};
  --tagBorderColour: ${brand.primary.pear.pear100};
  ${fonts.gotham.bold};
  font-size: ${fluid(12, 14)};
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 6px ${fluid(14, 16)};
  border-radius: 1000000000px;
  background-color: var(--tagBgColour);
  border: 2px solid var(--tagBorderColour);
  flex: 0 0 auto;
  height: ${fluid(30, 34)};

  &[data-tag-theme='teal'] {
    --tagBgColour: ${brand.primary.teal.teal50};
    --tagBorderColour: ${brand.primary.teal.teal100};
  }

  &[data-tag-theme='forrest-green'] {
    --tagBgColour: ${brand.accent.forrestGreen.forrestGreen50};
    --tagBorderColour: ${brand.accent.forrestGreen.forrestGreen100};
  }

  &[data-tag-theme='blue'] {
    --tagBgColour: ${brand.primary.blue.blue50};
    --tagBorderColour: ${brand.primary.blue.blue100};
  }

  &[data-tag-theme='yellow'] {
    --tagBgColour: ${brand.secondary.yellow.yellow50};
    --tagBorderColour: ${brand.secondary.yellow.yellow100};
  }

  &[data-tag-theme='dark-blue'] {
    --tagBgColour: ${brand.accent.darkBlue.darkBlue50};
    --tagBorderColour: ${brand.accent.darkBlue.darkBlue100};
  }

  &[data-tag-theme='bronze'] {
    --tagBgColour: ${brand.awards.bronze.bronze50};
    --tagBorderColour: ${brand.awards.bronze.bronze100};
  }

  &[data-tag-theme='silver'] {
    --tagBgColour: ${brand.awards.silver.silver50};
    --tagBorderColour: ${brand.awards.silver.silver100};
  }

  &[data-tag-theme='gold'] {
    --tagBgColour: ${brand.awards.gold.gold50};
    --tagBorderColour: ${brand.awards.gold.gold100};
  }

  &[data-tag-theme='orange'] {
    --tagBgColour: ${brand.secondary.orange.orange50};
    --tagBorderColour: ${brand.secondary.orange.orange100};
  }
`;
const TagsContainer = styled.div `
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex-wrap: wrap;
`;
const Tag = styled.div `
  --tagBgColour: ${brand.secondary.orange.orange50};
  --tagBorderColour: ${brand.secondary.orange.orange100};
  display: flex;
  gap: 6px;
  align-items: center;
  padding: 6px ${fluid(12, 16)};
  border-radius: 1000000000px;
  background-color: var(--tagBgColour);
  border: 2px solid var(--tagBorderColour);
  height: ${fluid(28, 34)};

  &[data-tag-theme='teal'] {
    --tagBgColour: ${brand.primary.teal.teal50};
    --tagBorderColour: ${brand.primary.teal.teal100};
  }

  &[data-tag-theme='forrest-green'] {
    --tagBgColour: ${brand.accent.forrestGreen.forrestGreen50};
    --tagBorderColour: ${brand.accent.forrestGreen.forrestGreen100};
  }

  &[data-tag-theme='blue'] {
    --tagBgColour: ${brand.primary.blue.blue50};
    --tagBorderColour: ${brand.primary.blue.blue100};
  }

  &[data-tag-theme='yellow'] {
    --tagBgColour: ${brand.secondary.yellow.yellow50};
    --tagBorderColour: ${brand.secondary.yellow.yellow100};
  }

  &[data-tag-theme='dark-blue'] {
    --tagBgColour: ${brand.accent.darkBlue.darkBlue50};
    --tagBorderColour: ${brand.accent.darkBlue.darkBlue100};
  }

  &[data-tag-theme='bronze'] {
    --tagBgColour: ${brand.awards.bronze.bronze50};
    --tagBorderColour: ${brand.awards.bronze.bronze100};
  }

  &[data-tag-theme='silver'] {
    --tagBgColour: ${brand.awards.silver.silver50};
    --tagBorderColour: ${brand.awards.silver.silver100};
  }

  &[data-tag-theme='gold'] {
    --tagBgColour: ${brand.awards.gold.gold50};
    --tagBorderColour: ${brand.awards.gold.gold100};
  }

  &[data-tag-theme='pear'] {
    --tagBgColour: ${brand.primary.pear.pear50};
    --tagBorderColour: ${brand.primary.pear.pear100};
  }
`;
const TagTitle = styled.p `
  ${paragraphSmall};
  ${fonts.gotham.bold};
  font-size: ${fluid(12, 14)};
  line-height: ${fluid(24, 26)};
  margin: 0;

  [data-type='engage'] & {
    ${fonts.plusJakarta.medium};
  }
`;
const Content = styled.div `
  ${paragraphSmall};
  font-size: ${fluid(14, 16)};
  line-height: ${fluid(20, 24)};
  margin-bottom: ${fluid(12, 16)};

  ${contentStyles};

  p {
    font-size: ${fluid(14, 16)};
    line-height: ${fluid(20, 24)};
    margin-bottom: ${fluid(12, 16)};
  }

  [data-type='engage'] & {
    ${fonts.plusJakarta.regular};
  }
`;
const HashTagArea = styled.div `
  display: flex;
  gap: 4px 16px;
  flex-wrap: wrap;
`;
const HashTag = styled.span `
  ${fonts.gotham.regular};
  font-size: ${fluid(16, 18)};
  line-height: ${fluid(24, 28)};
  opacity: 0.5;
`;
const ResourceCardStyles = {
    Container,
    ImageContainer,
    LinkArea,
    ImageWrapper,
    Image,
    CollectionTag,
    ButtonArea,
    EyeClose,
    EyeOpen,
    ViewResourceButton,
    DownloadResourceButton,
    ContentContainer,
    TitleArea,
    TagsContainer,
    Tag,
    TagTitle,
    Title,
    CardLink,
    AgeTag,
    Content,
    HashTagArea,
    HashTag,
};
export default ResourceCardStyles;
